import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { getProducts, getProductBySku, getProductById } from '../../../services/ProductService';
import SalesPanel from './SalesPanel';
import OptionalsModal from './billing_modal/OptionalsModal';
import { formatMoney } from '../../../utils/currency';

import SnackbarUtils from '../../../utils/SnackbarUtils';
import { canAddToTicket, MIXED_CURRENCY_MESSAGE } from './ticketCurrency';
const RetailSalesTerminal = ({ locationId, initialStoreCreditCode, onStoreCreditConsumed }) => {
  const [cartItems, setCartItems] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [skuError, setSkuError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [optionalsModalProduct, setOptionalsModalProduct] = useState(null);
  const barcodeInputRef = useRef(null);
  const barcodeValueRef = useRef(''); // valor actual del input (para no depender del estado cuando el lector envía Enter muy rápido)
  const scanDebounceRef = useRef(null); // timeout para detectar fin de escaneo
  const barcodeBurstStartRef = useRef(null); // cuándo empezó esta "ráfaga" (primer carácter) — solo auto-submit si fue todo de una

  const loadProducts = useCallback(
    async (search = '') => {
      setLoadingProducts(true);
      try {
        const data = await getProducts(locationId, 1, 500, search);
        const list = data?.products ?? data?.data ?? (Array.isArray(data) ? data : []);
        setProducts(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading products:', err);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    },
    [locationId]
  );

  useEffect(() => {
    loadProducts(searchTerm);
  }, [locationId, searchTerm, loadProducts]);

  useEffect(() => {
    return () => {
      if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    };
  }, []);

  const handleAddToCart = (product, optionalPayload = null) => {
    if (!canAddToTicket(cartItems, product)) {
      SnackbarUtils.warning(MIXED_CURRENCY_MESSAGE);
      return;
    }
    const hasOptionals = optionalPayload?.optionalSelections?.length > 0;
    // Por defecto se cobra el precio del canal Local (con fallback al precio base);
    // el "extra" de opcionales se guarda aparte para poder recalcular al cambiar el origen de la venta.
    const basePrice = product.priceLocal ?? product.price;
    const extrasAmount = optionalPayload?.unitPrice != null ? optionalPayload.unitPrice - product.price : 0;
    const unitPrice = basePrice + extrasAmount;
    const optionalSelections = optionalPayload?.optionalSelections ?? null;

    const updatedItems = (() => {
      if (hasOptionals) {
        return [...cartItems, { ...product, qty: 1, unitPrice, extrasAmount, optionalSelections }];
      }
      const existing = cartItems.find(
        (item) => item.id === product.id && !(item.optionalSelections?.length > 0)
      );
      if (existing) {
        return cartItems.map((item) =>
          item.id === product.id && !(item.optionalSelections?.length > 0)
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...cartItems, { ...product, qty: 1, unitPrice, extrasAmount, optionalSelections }];
    })();

    setCartItems((prev) => {
      const withLoadingFlag = updatedItems.map((item) => ({ ...item, _triggerLoading: true }));
      return withLoadingFlag;
    });
  };

  const handleProductClick = (product) => {
    getProductById(product.id, locationId)
      .then((fullProduct) => {
        const p = fullProduct || product;
        const optionals = p.optionals ?? [];
        const maxOpt = p.maxOptionalSelections ?? null;
        if (optionals.length > 0 && (maxOpt == null || maxOpt > 0)) {
          setOptionalsModalProduct(p);
        } else {
          handleAddToCart(p);
        }
      })
      .catch(() => handleAddToCart(product));
  };

  const handleBarcodeSubmit = async (valueOverride) => {
    const sku = (valueOverride !== undefined ? valueOverride : barcodeInput).trim();
    if (!sku) return;
    if (scanDebounceRef.current) {
      clearTimeout(scanDebounceRef.current);
      scanDebounceRef.current = null;
    }
    setSkuError(null);
    const product = await getProductBySku(locationId, sku);
    if (product) {
      const fullProduct = await getProductById(product.id, locationId).catch(() => product);
      const p = fullProduct || product;
      const optionals = p.optionals ?? [];
      const maxOpt = p.maxOptionalSelections ?? null;
      if (optionals.length > 0 && (maxOpt == null || maxOpt > 0)) {
        setOptionalsModalProduct(p);
        setBarcodeInput('');
        barcodeValueRef.current = '';
        barcodeBurstStartRef.current = null;
        barcodeInputRef.current?.focus();
      } else {
        handleAddToCart(p);
        setBarcodeInput('');
        barcodeValueRef.current = '';
        barcodeBurstStartRef.current = null;
        setSnackbar({ open: true, message: `Agregado: ${p.alias || p.name}`, severity: 'success' });
        barcodeInputRef.current?.focus();
      }
    } else {
      setSkuError('Producto no encontrado');
      setSnackbar({ open: true, message: 'No se encontró ningún producto con ese código de barras.', severity: 'error' });
    }
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSubmit(e.target.value);
    }
  };

  const handleBarcodeChange = (e) => {
    const value = e.target.value;
    const prevLen = (barcodeValueRef.current || '').length;
    setBarcodeInput(value);
    barcodeValueRef.current = value;
    setSkuError(null);
    if (value.length === 0) {
      barcodeBurstStartRef.current = null;
    } else if (prevLen === 0) {
      barcodeBurstStartRef.current = Date.now(); // empezó una "ráfaga"
    }
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    if (value.trim().length >= 4) {
      scanDebounceRef.current = setTimeout(() => {
        scanDebounceRef.current = null;
        const sku = barcodeValueRef.current.trim();
        const burstStart = barcodeBurstStartRef.current;
        // Solo auto-submit si entraron más de 3 caracteres "todos de una" (en menos de 400ms)
        const wasRapid = burstStart != null && (Date.now() - burstStart) < 400;
        if (sku && wasRapid) handleBarcodeSubmit(sku);
      }, 120);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden', minWidth: 0 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Escanear código de barras
          </Typography>
          <TextField
            inputRef={barcodeInputRef}
            fullWidth
            size="small"
            placeholder="Código de barras (Enter para agregar)"
            value={barcodeInput}
            onChange={handleBarcodeChange}
            onKeyDown={handleBarcodeKeyDown}
            error={!!skuError}
            helperText={skuError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerIcon color="action" />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </Paper>

        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Paper sx={{ flex: 1, overflow: 'auto', minHeight: 200 }}>
          <TableContainer sx={{ maxHeight: '100%' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="right">Código de barras</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="center" width={80}>
                    Agregar
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProducts ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }} color="text.secondary">
                      {searchTerm ? 'Sin resultados' : 'No hay productos en esta sucursal'}
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>{product.alias || product.name}</TableCell>
                      <TableCell align="right">{product.sku || '—'}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const displayPrice = product.priceLocal ?? product.price;
                          return typeof displayPrice === 'number' ? formatMoney(displayPrice, product.priceCurrency) : '—';
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleProductClick(product)}
                          title="Agregar al carrito"
                        >
                          <AddIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <SalesPanel locationId={locationId} items={cartItems} onUpdate={setCartItems} initialStoreCreditCode={initialStoreCreditCode} onStoreCreditConsumed={onStoreCreditConsumed} />
      </Box>

      <OptionalsModal
        open={Boolean(optionalsModalProduct)}
        product={optionalsModalProduct}
        locationId={locationId}
        onConfirm={(payload) => {
          if (optionalsModalProduct) {
            handleAddToCart(optionalsModalProduct, payload);
            setOptionalsModalProduct(null);
            setSnackbar({ open: true, message: `Agregado: ${optionalsModalProduct.alias || optionalsModalProduct.name}`, severity: 'success' });
          }
        }}
        onClose={() => setOptionalsModalProduct(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400 }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ alignItems: 'center', '& .MuiAlert-message': { alignSelf: 'center' } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetailSalesTerminal;
